import React, { useEffect, useState } from 'react';
import { compose, Dispatch } from 'redux';
import { connect } from 'react-redux';
import BEMHelper from 'react-bem-helper';

import { Container, Row, Col } from 'reactstrap';
import qs from 'qs';

// Elements
import Panel from '@/components/elements/Panel';
import Spinner from '@/components/elements/Spinner';
import { CompanyType, BookingType, Configuration, CheckoutStateType } from '@/types';
import * as actions from '@/actions';
import { Trans } from '@lingui/macro';
import Booking from '@/components/elements/Booking';
import api, { CreateCheckoutPartial } from '@/api/services';
import { getCountryCode, getTranslate } from '@/misc/common';

import './Completed.css';
import { ApplicationState } from '@/store';
import readConfigurationProperty from '@/misc/readConfigurationProperty';
import { useAppSelector } from '@/hooks';

interface Props {
    company: CompanyType;
    dispatch: Dispatch<any>;
    configuration: Configuration;
    checkout: CheckoutStateType;
}

const c = new BEMHelper({ name: 'Completed' });

export function PaymentFailed({ company, configuration, checkout, dispatch }: Props) {
    const [booking, setBooking] = useState<BookingType>();
    const [isPayClicked, setIsPayClicked] = useState(false);
    const localize = useAppSelector((s) => s.localize);
    const translate = getTranslate(localize);
    const paymentEnabled = booking?.Service?.IsPaymentEnabled;

    const payWithBillmate = company?.BookingSettings?.PaymentProviderId === 3;

    const statusRequiresPayment = booking?.StatusId === 5 || booking?.StatusId === 6;

    const needToPay = paymentEnabled && statusRequiresPayment;

    const { cancellationCode, referenceId } = qs.parse(document.location.search, {
        ignoreQueryPrefix: true,
    });

    useEffect(() => {
        dispatch(actions.fetchCompanyAsync.request());
        if (referenceId && cancellationCode) {
            api
                .getBooking({
                    Id: referenceId,
                    CancellationCode: cancellationCode,
                    IncludeCustomerInformation: true,
                })
                .then((response) => {
                    setBooking(response.Results[0]);
                })
                .catch((err) => console.log(err));
        }
    }, [referenceId, cancellationCode]);

    if (!booking || !company) return null;

    const companyName = company.Name || translate("completed.error.companyNameDefault");
    const completedErrorTemplate = translate("completed.error");
    const completedErrorMessage = completedErrorTemplate
        ? completedErrorTemplate.replace("{companyName}", companyName)
        : `Betalningen kunde inte laddas, vänligen kontakta ${companyName}`;

    if (checkout.hasError) {
        return (
            <Panel>
                <Row>
                    <Col xs={12}>
                        {completedErrorMessage}
                    </Col>
                </Row>
                {checkout?.error?.ResponseStatus?.Message ? (
                    <Row>
                        <Col xs={12}>{checkout.error.ResponseStatus?.Message}</Col>
                    </Row>
                ) : null}
            </Panel>
        );
    }

    if (!company) {
        return (
            <Container fluid>
                <Spinner noGraceTime noColor />
            </Container>
        );
    }
    const targetOrigin = configuration?.targetOrigin
        ? configuration?.targetOrigin
        : configuration?._targetOrigin;

    const onCancel = targetOrigin ? () => {
        // @ts-ignore
        window.location.replace(targetOrigin);
    } : undefined;

    // Condition 1 - not clicked
    const heading = isPayClicked ? <Trans id="pay" /> : <Trans id="paymentFailed" />


    return (
        <Container className="p-3">
            <h2 className="text-center mb-3">
                {heading}
            </h2>
            {checkout.isLoading && (
                <div className="hidden-print">
                    <Spinner {...c("paymentLoading")}>
                        <Trans id="yourBookingMustBePaid"></Trans>
                    </Spinner>
                </div>
            )}

            {checkout.data && payWithBillmate && (
                <iframe
                    src={checkout.data.Url}
                    data-testid="billmateContainer"
                    width="100%"
                    height="810px"
                    frameBorder="0"
                ></iframe>
            )}

            {!checkout.data && booking && configuration ? (
                <Booking
                    company={company}
                    booking={booking}
                    onPay={() => {

                        setIsPayClicked(true);

                        if (company.Id && company?.BookingSettings?.PaymentProviderId) {
                            const payload: CreateCheckoutPartial = {
                                CompanyId: company.Id,
                                Id: booking.Id,
                                PaymentProviderId: company.BookingSettings.PaymentProviderId,
                                CountryCode: getCountryCode(readConfigurationProperty('language', 'se')),
                                Articles: [
                                    {
                                        ArticleTypeId: 1,
                                        //@ts-ignore
                                        InternalReferenceId: booking.Id,
                                        Quantity: 1
                                    }
                                ],
                                customerEmail: booking.Customer.Email
                            };

                            dispatch({
                                type: "CREATE_CHECKOUT_REQUEST",
                                payload,
                            });

                        }

                    }}
                    onCancel={onCancel}
                />
            ) : null}
        </Container>
    );
}


export default compose<React.ComponentType>(
    connect(({ company, configuration, checkout }: ApplicationState) => ({
        company: company.data,
        configuration: configuration.data,
        checkout
    }))
)(PaymentFailed);
